/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ActionFiringMode } from "../../flatbuffers/generated/archive/action-firing-mode";
// INTERFACES
import { LiftInterface } from "../../interfaces/Lift.interface";
import { BankInterface } from "../../interfaces/Bank.interface";
import {
  MonitoringViewTypeEnum,
  MonitoringDeviceConfigurationInterface,
} from "../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_LIFT } from "../../constants/lifts";
import { BLANK_BANK } from "../../constants/bank";

/**
 * State of the monitoring slice: the monitoring currently being viewed or edited,
 * the active selection, and the open/closed flags for this area's modals.
 */
export interface MonitoringState {
  openDrawerLiftActions: boolean;
  liftSelected: LiftInterface;
  bankSelected: BankInterface;
  channelId: string;
  serverId: string;
  controlPanelTab: {
    liftType: string;
    tab: string;
  };
  controlPanelDrawerPlacement: "right" | "left";
  openModalControlPanelLift: boolean;
  openModalControlPanelBank: boolean;
  openModalSaveGrid: boolean;
  openModalManageGrids: boolean;
  enableDragAndDrop: boolean;
  showFullView: boolean;
  monitoringGridType: MonitoringViewTypeEnum;
  idGridSelected: string;
  cascaderSelectMultipleSelectionOptions: [];
  topViewLayout: [];
  detailViewLayout: [];
  statusViewLayout: [];
  serviceViewLayout: [];
  refreshPage: boolean;
  sendNewMessage: any;
  showQueueInfo: boolean;
  openGlobalTerminal: boolean;
  openGlobalEndpointPanel: boolean;
  totpIsValid: boolean;
  monitorViewIsLoading: boolean;
  socketConnection: string;
  edgeServerIsConnected: boolean;
  emulatorIsConnected: boolean;
  terminalResetTable: boolean;
  terminalDownloadReport: boolean;
  favoriteGridName: string;
  hardcodedCommands: {
    id: string;
    enum_id: number;
    message_type_id: number;
    device_type: string;
    message_content_type: number;
    short_message: string;
    description: string;
  }[];
  clearCascader: boolean;
  showHeaderConfiguration: boolean;
  openMonitoringDeviceConfigurationDrawer: boolean;
  monitoringDeviceConfiguration: MonitoringDeviceConfigurationInterface;
  terminalAutoScroll: boolean;
  showTerminalTable: boolean;
  showTerminalJsonViewer: boolean;
  openReplayPanel: boolean;
  /** The Command Sequences drawer (master admins). */
  openCommandSequences: boolean;
  replayFilterBankId: string;
  replayScannedBankIds: string[];
  terminalShowCarHallCalls: boolean;
  stopSocketConnection: boolean;
  terminalShowBankFloors: boolean;
  terminalIconMode: "svg" | "text";
  terminalMode: "websocket" | "endpoint";
  /** Render the Terminal and the monitoring grid together instead of swapping one for the other. */
  terminalSplitView: boolean;
  /** "horizontal" stacks the panes (grid above terminal); "vertical" puts them side by side. */
  terminalSplitOrientation: "horizontal" | "vertical";
  /** Share of the split container given to the grid pane, as a percentage. */
  terminalSplitRatio: number;
  /** Include outbound (TX) UiCmd rows in the Terminal message table. */
  terminalShowTxMessages: boolean;
  /** Show the bank/lift command buttons above the Terminal message table. */
  showTerminalCommandPanel: boolean;
  doorUnknownAsOpening: boolean;
  hideUnconfiguredDoors: boolean;
  /**
   * What the operator has CONFIGURED for the logged-in user — their own
   * override if they have one, otherwise their organization's setting, and
   * INHERIT when neither has set anything.
   *
   * INHERIT is stored rather than resolved away, because the two surfaces have
   * DIFFERENT defaults: the Car Calls pad has always sent on the first click
   * and an Actions card has always staged. Collapsing "nobody chose" into a
   * behaviour here would change one of them on upgrade. Call
   * firesOnFloorClick(mode, surface) instead of comparing this directly.
   */
  actionFiringMode: ActionFiringMode;
  binaryCaptureActive: boolean;
  binaryCaptureReady: boolean;
  mockMovements: boolean;
  autoSortGrid: string;
  previewFaultServiceMode: boolean;
  showCommandDetails: boolean;
  sendCommand: {
    sendCommand: boolean;
    serverId: string;
    body: {
      command: any;
      position: number;
      active: boolean;
      bankId: string;
      liftId: string;
    };
  };
}

const normalizeSocketMode = (
  configuredMode: string | undefined,
): "EDGE" | "OFFLINE" | "EMULATOR" | "" => {
  switch (configuredMode) {
    case "DEVELOPMENT":
    case "EDGE":
      return "EDGE";
    case "OFFLINE":
      return "OFFLINE";
    case "EMULATOR":
      return "EMULATOR";
    default:
      return "";
  }
};

const initialState: MonitoringState = {
  openDrawerLiftActions: false,
  liftSelected: { ...BLANK_LIFT },
  bankSelected: { ...BLANK_BANK },
  controlPanelTab: {
    liftType: "ELEVATOR",
    tab: "FAULTS",
  },
  controlPanelDrawerPlacement: "right",
  openModalControlPanelLift: false,
  openModalControlPanelBank: false,
  openModalSaveGrid: false,
  openModalManageGrids: false,
  serverId: "",
  channelId: "",
  enableDragAndDrop: false,
  showFullView: false,
  monitoringGridType: 0,
  idGridSelected: "",
  cascaderSelectMultipleSelectionOptions: [],
  topViewLayout: [],
  detailViewLayout: [],
  statusViewLayout: [],
  serviceViewLayout: [],
  refreshPage: false,
  sendNewMessage: {},
  showQueueInfo: false,
  openGlobalTerminal: false,
  openGlobalEndpointPanel: localStorage.getItem("endpointMonitorOpen") === "true",
  totpIsValid: false,
  monitorViewIsLoading: false,
  socketConnection: normalizeSocketMode(process.env.REACT_APP_EDGE_SERVER_CONNECTION),
  edgeServerIsConnected: false,
  emulatorIsConnected: false,
  terminalResetTable: false,
  terminalDownloadReport: false,
  favoriteGridName: "",
  hardcodedCommands: [],
  clearCascader: false,
  showHeaderConfiguration: true,
  openMonitoringDeviceConfigurationDrawer: false,
  monitoringDeviceConfiguration: {
    showElevators: true,
    showEscalators: true,
    showMovingwalks: true,
    showGpio: true,
    viewType: "server",
  },
  terminalAutoScroll: false,
  showTerminalTable: true,
  showTerminalJsonViewer: true,
  openReplayPanel: false,
  openCommandSequences: false,
  replayFilterBankId: "",
  replayScannedBankIds: [],
  terminalShowCarHallCalls: false,
  stopSocketConnection: false,
  terminalShowBankFloors: false,
  terminalIconMode: "svg" as "svg" | "text",
  terminalMode: "websocket" as "websocket" | "endpoint",
  terminalSplitView: false,
  terminalSplitOrientation: "horizontal" as "horizontal" | "vertical",
  terminalSplitRatio: 50,
  terminalShowTxMessages: true,
  showTerminalCommandPanel: true,
  doorUnknownAsOpening: true,
  hideUnconfiguredDoors: true,
  // INHERIT: nothing configured until hydration says otherwise, so each
  // surface keeps its own long-standing default in the meantime.
  actionFiringMode: ActionFiringMode.INHERIT,
  binaryCaptureActive: false,
  binaryCaptureReady: false,
  mockMovements: false,
  autoSortGrid: "",
  previewFaultServiceMode: false,
  showCommandDetails: false,
  sendCommand: {
    sendCommand: false,
    serverId: "",
    body: { command: null, position: 0, active: true, bankId: "", liftId: "" },
  },
};

export const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    updateOpenDrawerLiftActions: (state, action: PayloadAction<boolean>) => {
      state.openDrawerLiftActions = action.payload;
    },
    updateLiftSelected: (state, action: PayloadAction<LiftInterface>) => {
      state.liftSelected = action.payload;
    },
    updateBankSelected: (state, action: PayloadAction<BankInterface>) => {
      state.bankSelected = action.payload;
    },
    updateModalControlPanelTab: (state, action: PayloadAction<any>) => {
      state.controlPanelTab = action.payload;
    },
    updateOpenModalControlPanelLift: (state, action: PayloadAction<boolean>) => {
      state.openModalControlPanelLift = action.payload;
    },
    updateOpenModalControlPanelBank: (state, action: PayloadAction<boolean>) => {
      state.openModalControlPanelBank = action.payload;
    },
    updateOpenModalSaveGrid: (state, action: PayloadAction<boolean>) => {
      state.openModalSaveGrid = action.payload;
    },
    updateOpenModalManageGrids: (state, action: PayloadAction<boolean>) => {
      state.openModalManageGrids = action.payload;
    },
    updateServerId: (state, action: PayloadAction<string>) => {
      state.serverId = action.payload;
    },
    updateChannelId: (state, action: PayloadAction<string>) => {
      state.channelId = action.payload;
    },
    updateEnableDragAndDrop: (state, action: PayloadAction<boolean>) => {
      state.enableDragAndDrop = action.payload;
    },
    updateShowFullView: (state, action: PayloadAction<boolean>) => {
      state.showFullView = action.payload;
    },
    updateMonitoringGridType: (state, action: PayloadAction<MonitoringViewTypeEnum>) => {
      state.monitoringGridType = action.payload;
    },
    updateIdGridSelected: (state, action: PayloadAction<string>) => {
      state.idGridSelected = action.payload;
    },
    updateCascaderSelectMultipleSelectionOptions: (state, action: PayloadAction<any>) => {
      state.cascaderSelectMultipleSelectionOptions = action.payload;
    },
    updateTopViewLayout: (state, action: PayloadAction<any>) => {
      state.topViewLayout = action.payload;
    },
    updateDetailViewLayout: (state, action: PayloadAction<any>) => {
      state.detailViewLayout = action.payload;
    },
    updateStatusViewLayout: (state, action: PayloadAction<any>) => {
      state.statusViewLayout = action.payload;
    },
    updateServiceViewLayout: (state, action: PayloadAction<any>) => {
      state.serviceViewLayout = action.payload;
    },
    updateRefreshPage: (state, action: PayloadAction<boolean>) => {
      state.refreshPage = action.payload;
    },
    updateSendNewMessage: (state, action: PayloadAction<any>) => {
      state.sendNewMessage = action.payload;
    },
    updateShowQueueInfo: (state, action: PayloadAction<boolean>) => {
      state.showQueueInfo = action.payload;
    },
    updateOpenGlobalTerminal: (state, action: PayloadAction<boolean>) => {
      state.openGlobalTerminal = action.payload;
    },
    updateOpenGlobalEndpointPanel: (state, action: PayloadAction<boolean>) => {
      state.openGlobalEndpointPanel = action.payload;
      localStorage.setItem("endpointMonitorOpen", String(action.payload));
    },
    updateSocketConnection: (state, action: PayloadAction<string>) => {
      state.socketConnection = action.payload;
    },
    updateTotpIsValid: (state, action: PayloadAction<boolean>) => {
      state.totpIsValid = action.payload;
    },
    updateMonitorViewIsLoading: (state, action: PayloadAction<boolean>) => {
      state.monitorViewIsLoading = action.payload;
    },
    updateEdgeServerIsConnected: (state, action: PayloadAction<boolean>) => {
      state.edgeServerIsConnected = action.payload;
    },
    updateEmulatorIsConnected: (state, action: PayloadAction<boolean>) => {
      state.emulatorIsConnected = action.payload;
    },
    updateTerminalResetTable: (state, action: PayloadAction<boolean>) => {
      state.terminalResetTable = action.payload;
    },
    updateTerminalDownloadReport: (state, action: PayloadAction<boolean>) => {
      state.terminalDownloadReport = action.payload;
    },
    updateControlPanelDrawerPlacement: (state, action: PayloadAction<"right" | "left">) => {
      state.controlPanelDrawerPlacement = action.payload;
    },
    updateFavoriteGridName: (state, action: PayloadAction<string>) => {
      state.favoriteGridName = action.payload;
    },
    updateClearCascader: (state, action: PayloadAction<boolean>) => {
      state.clearCascader = action.payload;
    },
    updateShowHeaderConfiguration: (state, action: PayloadAction<boolean>) => {
      state.showHeaderConfiguration = action.payload;
    },
    updateOpenMonitoringDeviceConfigurationDrawer: (state, action: PayloadAction<boolean>) => {
      state.openMonitoringDeviceConfigurationDrawer = action.payload;
    },
    updateMonitoringDeviceConfiguration: (
      state,
      action: PayloadAction<MonitoringDeviceConfigurationInterface>,
    ) => {
      state.monitoringDeviceConfiguration = action.payload;
    },
    updateTerminalAutoScroll: (state, action: PayloadAction<boolean>) => {
      state.terminalAutoScroll = action.payload;
    },
    updateShowTerminalJsonViewer: (state, action: PayloadAction<boolean>) => {
      state.showTerminalJsonViewer = action.payload;
    },
    updateShowTerminalTable: (state, action: PayloadAction<boolean>) => {
      state.showTerminalTable = action.payload;
    },
    updateOpenReplayPanel: (state, action: PayloadAction<boolean>) => {
      state.openReplayPanel = action.payload;
      if (!action.payload) {
        state.replayFilterBankId = "";
        state.replayScannedBankIds = [];
      }
    },
    updateOpenCommandSequences: (state, action: PayloadAction<boolean>) => {
      state.openCommandSequences = action.payload;
    },
    updateReplayFilterBankId: (state, action: PayloadAction<string>) => {
      state.replayFilterBankId = action.payload;
    },
    updateReplayScannedBankIds: (state, action: PayloadAction<string[]>) => {
      state.replayScannedBankIds = action.payload;
    },
    updateTerminalShowCarHallCalls: (state, action: PayloadAction<boolean>) => {
      state.terminalShowCarHallCalls = action.payload;
    },
    updateStopSocketConnection: (state, action: PayloadAction<boolean>) => {
      state.stopSocketConnection = action.payload;
    },
    updateTerminalShowBankFloors: (state, action: PayloadAction<boolean>) => {
      state.terminalShowBankFloors = action.payload;
    },
    updateTerminalIconMode: (state, action: PayloadAction<"svg" | "text">) => {
      state.terminalIconMode = action.payload;
    },
    updateDoorUnknownAsOpening: (state, action: PayloadAction<boolean>) => {
      state.doorUnknownAsOpening = action.payload;
    },
    updateActionFiringMode: (state, action: PayloadAction<ActionFiringMode>) => {
      state.actionFiringMode = action.payload;
    },
    updateHideUnconfiguredDoors: (state, action: PayloadAction<boolean>) => {
      state.hideUnconfiguredDoors = action.payload;
    },
    updateBinaryCaptureActive: (state, action: PayloadAction<boolean>) => {
      state.binaryCaptureActive = action.payload;
    },
    updateBinaryCaptureReady: (state, action: PayloadAction<boolean>) => {
      state.binaryCaptureReady = action.payload;
    },
    updateMockMovements: (state, action: PayloadAction<boolean>) => {
      state.mockMovements = action.payload;
    },
    updateTerminalMode: (state, action: PayloadAction<"websocket" | "endpoint">) => {
      state.terminalMode = action.payload;
    },
    updateTerminalSplitView: (state, action: PayloadAction<boolean>) => {
      state.terminalSplitView = action.payload;
    },
    updateTerminalSplitOrientation: (state, action: PayloadAction<"horizontal" | "vertical">) => {
      state.terminalSplitOrientation = action.payload;
    },
    // Clamped so a drag can never collapse a pane to nothing — a zero-width
    // grid pane would leave the ResizeObserver in MonitoringGrid measuring 0
    // and the grid would never regenerate when the pane grows back.
    updateTerminalSplitRatio: (state, action: PayloadAction<number>) => {
      state.terminalSplitRatio = Math.min(85, Math.max(15, action.payload));
    },
    updateTerminalShowTxMessages: (state, action: PayloadAction<boolean>) => {
      state.terminalShowTxMessages = action.payload;
    },
    updateShowTerminalCommandPanel: (state, action: PayloadAction<boolean>) => {
      state.showTerminalCommandPanel = action.payload;
    },
    updateAutoSortGrid: (state, action: PayloadAction<string>) => {
      state.autoSortGrid = action.payload;
    },
    updatePreviewFaultServiceMode: (state, action: PayloadAction<boolean>) => {
      state.previewFaultServiceMode = action.payload;
    },
    updateShowCommandDetails: (state, action: PayloadAction<boolean>) => {
      state.showCommandDetails = action.payload;
    },
    updateSendCommand: (
      state,
      action: PayloadAction<{
        sendCommand: boolean;
        serverId: string;
        body: any;
      }>,
    ) => {
      state.sendCommand = action.payload;
    },
    updateHardCommands: (
      state,
      action: PayloadAction<
        {
          id: string;
          enum_id: number;
          message_type_id: number;
          device_type: string;
          message_content_type: number;
          short_message: string;
          description: string;
        }[]
      >,
    ) => {
      state.hardcodedCommands = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateOpenDrawerLiftActions,
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateOpenModalControlPanelBank,
  updateOpenModalSaveGrid,
  updateOpenModalManageGrids,
  updateServerId,
  updateChannelId,
  updateEnableDragAndDrop,
  updateShowFullView,
  updateMonitoringGridType,
  updateIdGridSelected,
  updateCascaderSelectMultipleSelectionOptions,
  updateDetailViewLayout,
  updateTopViewLayout,
  updateStatusViewLayout,
  updateServiceViewLayout,
  updateRefreshPage,
  updateSendNewMessage,
  updateShowQueueInfo,
  updateSocketConnection,
  updateOpenGlobalTerminal,
  updateOpenGlobalEndpointPanel,
  updateTotpIsValid,
  updateMonitorViewIsLoading,
  updateEdgeServerIsConnected,
  updateEmulatorIsConnected,
  updateTerminalResetTable,
  updateTerminalDownloadReport,
  updateControlPanelDrawerPlacement,
  updateFavoriteGridName,
  updateHardCommands,
  updateClearCascader,
  updateOpenMonitoringDeviceConfigurationDrawer,
  updateMonitoringDeviceConfiguration,
  updateShowHeaderConfiguration,
  updateTerminalAutoScroll,
  updateShowTerminalJsonViewer,
  updateShowTerminalTable,
  updateOpenReplayPanel,
  updateOpenCommandSequences,
  updateReplayFilterBankId,
  updateReplayScannedBankIds,
  updateTerminalShowCarHallCalls,
  updateStopSocketConnection,
  updateTerminalShowBankFloors,
  updateTerminalIconMode,
  updateDoorUnknownAsOpening,
  updateActionFiringMode,
  updateHideUnconfiguredDoors,
  updateBinaryCaptureActive,
  updateBinaryCaptureReady,
  updateMockMovements,
  updateTerminalMode,
  updateTerminalSplitView,
  updateTerminalSplitOrientation,
  updateTerminalSplitRatio,
  updateTerminalShowTxMessages,
  updateShowTerminalCommandPanel,
  updateAutoSortGrid,
  updatePreviewFaultServiceMode,
  updateShowCommandDetails,
  updateSendCommand,
} = monitoringSlice.actions;

export default monitoringSlice.reducer;
